export type FixedAssetStatus = 'ACTIVE' | 'FULLY_DEPRECIATED' | 'DISPOSED';
export type AcquisitionSource = 'NEW_PURCHASE' | 'REGISTERED_EXISTING' | 'SUPPLIER_BILL';
export type DepreciationMethod = 'STRAIGHT_LINE' | 'REDUCING_BALANCE';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
export const FIXED_ASSET_STATUS_COLORS: Record<FixedAssetStatus, ChipColor> = {
  ACTIVE: 'success',
  FULLY_DEPRECIATED: 'info',
  DISPOSED: 'default',
};

export interface FixedAsset {
  id: string;
  institutionId: string;
  assetCode: string;
  name: string;
  description?: string;
  category?: string;
  acquisitionDate: string;
  acquisitionCost: number;
  salvageValue: number;
  usefulLifeMonths: number;
  depreciationMethod: DepreciationMethod;
  depreciationStartDate: string;
  segmentId?: string;
  assetAccountId?: string;
  accumulatedDepreciationAccountId?: string;
  depreciationExpenseAccountId?: string;
  accumulatedDepreciation: number;
  depreciatedThrough?: string;
  status: FixedAssetStatus;
  acquisitionSource: AcquisitionSource;
  acquisitionJournalEntryId?: string;
  disposalDate?: string;
  disposalProceeds?: number;
  disposalJournalEntryId?: string;
  // W3-K
  categoryId?: string;
  depreciationRatePct?: number;
  // B4-E: figures after revaluation / impairment (acquisitionCost and accumulatedDepreciation stay historical)
  grossCost?: number;
  glAccumulatedDepreciation?: number;
  accumulatedImpairment?: number;
  carryingAmount?: number;
  // ---- F10: placement (names resolved by the server; null when unassigned) ----
  locationId?: string | null;
  locationName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  custodianUserId?: string | null;
  custodianName?: string | null;
}

/** Net book value after any revaluation or impairment (falls back to cost less depreciation for older payloads). */
export const assetCarrying = (a: Pick<FixedAsset, 'acquisitionCost' | 'accumulatedDepreciation' | 'carryingAmount'>): number =>
  a.carryingAmount ?? (a.acquisitionCost - a.accumulatedDepreciation);

// new-purchase create (posts DR asset / CR creditAccountId). Field names match backend FixedAssetCreateDTO.
export interface FixedAssetCreateDTO {
  institutionId: string;
  name: string;
  description?: string;
  category?: string;
  acquisitionDate: string;
  acquisitionCost: number;
  salvageValue: number;
  usefulLifeMonths: number;
  depreciationStartDate: string;
  segmentId?: string;
  creditAccountId?: string;           // cash / payable / inventory; required for the accounts team, others use the settings default
  assetAccountId?: string;            // override (else from settings)
  accumulatedDepreciationAccountId?: string;
  depreciationExpenseAccountId?: string;
  // W3-K: category defaults fill any blank; method/rate (reducing balance needs the rate)
  categoryId?: string;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePct?: number;
  // ---- F10: optional initial placement (validated against the institution) ----
  locationId?: string;
  departmentId?: string;
  custodianUserId?: string;
}

// register-existing (posts an opening JE dated openingDate). Field names match backend FixedAssetRegisterDTO:
// openingDate + openingAccumulatedDepreciation are required; usefulLifeMonths is REMAINING life.
export interface FixedAssetRegisterDTO {
  institutionId: string;
  name: string;
  description?: string;
  category?: string;
  acquisitionDate: string;
  acquisitionCost: number;
  salvageValue: number;
  usefulLifeMonths: number;           // remaining (going-forward) months
  depreciationStartDate: string;
  segmentId?: string;
  assetAccountId?: string;
  accumulatedDepreciationAccountId?: string;
  depreciationExpenseAccountId?: string;
  openingDate: string;                // go-live date the opening JE is dated at (must be in an open period)
  openingAccumulatedDepreciation: number;  // prior-system accumulated carried in
  depreciatedThrough?: string;        // first-of-month already-depreciated marker
  categoryId?: string;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePct?: number;
}

// Edit — backend FixedAssetEditDTO (name required). An omitted account keeps the asset's current one;
// a changed account applies to future postings only (past journals stay on the old account).
export interface FixedAssetEditDTO {
  name: string;
  description?: string;
  category?: string;
  assetAccountId?: string;
  accumulatedDepreciationAccountId?: string;
  depreciationExpenseAccountId?: string;
  categoryId?: string;
  depreciationMethod?: DepreciationMethod;   // refused once depreciation has posted
  depreciationRatePct?: number;
}

// Field names match backend DisposeRequestDTO.
export interface DisposeDTO {
  disposalDate: string;
  proceeds: number;                   // 0 = write-off
  cashOrReceivableAccountId?: string; // cash / receivable; required for the accounts team, others use the settings default
}
